import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getProjects,
  getProjectsByRep,
  getClosedProjects,
  getUnpaidProjects,
  getProject,
  getAllProjects,
  linkReferralSource,
  unlinkReferralSource,
  markCommissionPaid,
  markCommissionUnpaid,
  markPartnerCommissionPaid,
  markPartnerCommissionUnpaid,
  updatePartnerExpectedCommission,
  updateProjectSalesRep,
  getProjectStats,
} from '@/services/projects';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import type { Business } from '@/types';

// ===========================================
// Query Keys
// ===========================================

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  allProjects: () => [...projectKeys.lists(), 'all'] as const,
  byBusiness: (business: Business) => [...projectKeys.lists(), { business }] as const,
  byRep: (business: Business, repId: string) =>
    [...projectKeys.lists(), { business, repId }] as const,
  closed: (business: Business, start: string, end: string) =>
    [...projectKeys.lists(), { business, start, end, status: 'closed' }] as const,
  unpaid: (business: Business) =>
    [...projectKeys.lists(), { business, status: 'unpaid' }] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  stats: (business: Business) => [...projectKeys.all, 'stats', business] as const,
};

// ===========================================
// Project Query Hooks
// ===========================================

/**
 * Fetch all projects for the current business
 */
export function useProjects() {
  const { currentBusiness } = useBusiness();

  return useQuery({
    queryKey: projectKeys.byBusiness(currentBusiness),
    queryFn: () => getProjects(currentBusiness),
  });
}

/**
 * Fetch projects for a specific business
 */
export function useProjectsByBusiness(business: Business) {
  return useQuery({
    queryKey: projectKeys.byBusiness(business),
    queryFn: () => getProjects(business),
  });
}

/**
 * Fetch projects for current user (rep view)
 */
export function useMyProjects() {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();

  return useQuery({
    queryKey: projectKeys.byRep(currentBusiness, user?.id || ''),
    queryFn: () => getProjectsByRep(currentBusiness, user?.id || ''),
    enabled: !!user?.id,
  });
}

/**
 * Fetch closed projects within a date range
 */
export function useClosedProjects(startDate: Date, endDate: Date) {
  const { currentBusiness } = useBusiness();

  return useQuery({
    queryKey: projectKeys.closed(
      currentBusiness,
      startDate.toISOString(),
      endDate.toISOString()
    ),
    queryFn: () => getClosedProjects(currentBusiness, startDate, endDate),
  });
}

/**
 * Fetch unpaid commission projects
 */
export function useUnpaidProjects() {
  const { currentBusiness } = useBusiness();

  return useQuery({
    queryKey: projectKeys.unpaid(currentBusiness),
    queryFn: () => getUnpaidProjects(currentBusiness),
  });
}

/**
 * Fetch a single project
 */
export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(id!),
    queryFn: () => getProject(id!),
    enabled: !!id,
  });
}

/**
 * Fetch project statistics
 */
export function useProjectStats() {
  const { currentBusiness } = useBusiness();

  return useQuery({
    queryKey: projectKeys.stats(currentBusiness),
    queryFn: () => getProjectStats(currentBusiness),
  });
}

// ===========================================
// Project Mutation Hooks
// ===========================================

/**
 * Link a referral source to a project
 */
export function useLinkReferralSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      referralSourceId,
      referralSourceName,
    }: {
      projectId: string;
      referralSourceId: string;
      referralSourceName: string;
    }) => linkReferralSource(projectId, referralSourceId, referralSourceName),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      toast.success('Referral source linked');
    },
    onError: (error) => {
      console.error('Link referral error:', error);
      toast.error('Failed to link referral source');
    },
  });
}

/**
 * Unlink a referral source from a project
 */
export function useUnlinkReferralSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unlinkReferralSource,
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      toast.success('Referral source unlinked');
    },
    onError: (error) => {
      console.error('Unlink referral error:', error);
      toast.error('Failed to unlink referral source');
    },
  });
}

/**
 * Mark commission as paid
 */
export function useMarkCommissionPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      paidAmount,
      paidDate,
    }: {
      projectId: string;
      paidAmount: number;
      paidDate?: Date;
    }) => markCommissionPaid(projectId, paidAmount, paidDate),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success('Commission marked as paid');
    },
    onError: (error) => {
      console.error('Mark paid error:', error);
      toast.error('Failed to mark commission as paid');
    },
  });
}

/**
 * Mark commission as unpaid (revert)
 */
export function useMarkCommissionUnpaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markCommissionUnpaid,
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success('Commission marked as unpaid');
    },
    onError: (error) => {
      console.error('Mark unpaid error:', error);
      toast.error('Failed to update commission status');
    },
  });
}

// ===========================================
// All Projects (Admin) Hook
// ===========================================

/**
 * Fetch ALL projects across all businesses (admin only)
 */
export function useAllProjects() {
  return useQuery({
    queryKey: projectKeys.allProjects(),
    queryFn: () => getAllProjects(),
  });
}

// ===========================================
// Partner Commission Mutation Hooks
// ===========================================

/**
 * Mark partner commission as paid
 */
export function useMarkPartnerCommissionPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      paidAmount,
      paidDate,
    }: {
      projectId: string;
      paidAmount: number;
      paidDate?: Date;
    }) => markPartnerCommissionPaid(projectId, paidAmount, paidDate),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success('Partner commission marked as paid');
    },
    onError: (error) => {
      console.error('Mark partner paid error:', error);
      toast.error('Failed to mark partner commission as paid');
    },
  });
}

/**
 * Mark partner commission as unpaid (revert)
 */
export function useMarkPartnerCommissionUnpaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markPartnerCommissionUnpaid,
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success('Partner commission marked as unpaid');
    },
    onError: (error) => {
      console.error('Mark partner unpaid error:', error);
      toast.error('Failed to update partner commission status');
    },
  });
}

/**
 * Update partner expected commission amount
 */
export function useUpdatePartnerExpectedCommission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      partnerExpectedCommission,
    }: {
      projectId: string;
      partnerExpectedCommission: number;
    }) => updatePartnerExpectedCommission(projectId, partnerExpectedCommission),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success('Partner commission updated');
    },
    onError: (error) => {
      console.error('Update partner commission error:', error);
      toast.error('Failed to update partner commission');
    },
  });
}

/**
 * Update project sales rep assignment
 */
export function useUpdateProjectSalesRep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      salesRepUserId,
      salesRepName,
    }: {
      projectId: string;
      salesRepUserId: string | null;
      salesRepName: string | null;
    }) => updateProjectSalesRep(projectId, salesRepUserId, salesRepName),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      toast.success('Sales rep updated successfully');
    },
    onError: (error) => {
      console.error('Update sales rep error:', error);
      toast.error('Failed to update sales rep');
    },
  });
}
